import React, { useMemo, useState } from 'react';
import useStyles from './useStyles';
import { CircularProgress } from '@material-ui/core';
import { useDropzone } from 'react-dropzone';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { CapaQuotationServiceType } from '@generated/graphql';
import { ServiceTypeInfo } from '@routes/RequestForQuotation/types';
import useRequest from '@hooks/useRequest';
import useFiles from '@hooks/useFiles';
import { clsx, useSnackbar } from '@components/Provider';
import { Enum_Action_Button_Enum } from '@hooks/useUserActionLog';
import { DialogTypes } from './types';
import jwtDecode from '@utils/jwtDecode';
// import useClient from '@hooks/useClient';
// import jwtDecode from '@utils/jwtDecode';

interface Props {
  serviceType: CapaQuotationServiceType;
  uploadedFilesCount: number;
  onOpenDialog: (type: keyof DialogTypes) => void;
}

const RequestForQuotationDropzone = ({
  uploadedFilesCount,
  serviceType,
  onOpenDialog
}: Props) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { onUploadFiles } = useFiles();
  const { onCreateRfqFile, requestState } = useRequest();

  const jwt = jwtDecode();

  const isConnectUser = jwt?.scopes.some((scope) => scope === 'CONNECT');

  const { getRootProps, getInputProps } = useDropzone({
    noClick: true,
    accept: serviceType ? ServiceTypeInfo[serviceType]?.accept : '',
    onDrop: async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const uploadedFiles =
        requestState.uploadedDrawingFiles &&
        requestState.uploadedDrawingFiles.length > 0
          ? requestState.uploadedDrawingFiles
          : [];

      const overSizeFiles = acceptedFiles?.filter(
        (file) => file.size > 100 * 1024 * 1024
      ).length;

      if (overSizeFiles > 0) {
        return enqueueSnackbar(
          `도면 파일은 1개당 100MB 이하의 파일만 업로드 가능합니다.`,
          { variant: 'error' }
        );
      }

      if (uploadedFiles.length + acceptedFiles.length > 20) {
        return enqueueSnackbar(`도면 파일은 20개까지 업로드 가능합니다.`, {
          variant: 'error'
        });
      }

      setLoading(true);

      const res = await onUploadFiles(acceptedFiles);

      if (res.error) {
        setLoading(false);
        return enqueueSnackbar(`파일 업로드 실패`, { variant: 'error' });
      }

      if (!res.error) {
        for (let file of res.files) {
          delete file?.oldFileId;
          delete file?.status;
          delete file?.fileSize;
          delete file?.fileMimetype;
          delete file?.fileExtension;

          onCreateRfqFile({ ...file, isValid: false });
        }
        setLoading(false);
        return enqueueSnackbar('파일 업로드 완료', { variant: 'success' });
      }
    },
    onDropRejected: () => {
      return enqueueSnackbar(
        `지원하는 확장자가 아닙니다. 지원 확장자 : ${ServiceTypeInfo[serviceType]?.accept}`,
        { variant: 'error' }
      );
    }
  });

  const onOpen = () => {
    const input = document.getElementById('file-dropzone');

    if (input) {
      input.click();
    }
  };

  const logId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_Upload_DrwaingFile;
    }
    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_Upload_DrwaingFile;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_Upload_DrwaingFile;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_Upload_DrwaingFile;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_Upload_DrwaingFile;
    }
  }, [serviceType]);

  return (
    <div
      {...getRootProps({
        className: clsx(classes['rfq-dropzone'], {
          'empty': uploadedFilesCount === 0,
          'inprogress': loading
        })
      })}
    >
      <input id="file-dropzone" type="file" {...getInputProps()} />

      {loading && <CircularProgress color="primary" />}

      {!loading && (
        <React.Fragment>
          <div className="rfq-dropzone-text">
            <CustomizedTypography
              variant="body1"
              color="textPrimary"
              fontWeight="500"
            >
              도면 파일을 이곳에 드래그 <span>또는</span>
            </CustomizedTypography>
          </div>

          <div className="rfq-dropzone-button-group">
            <CustomizedButton
              logId={logId}
              color="primary"
              variant="text"
              onClick={onOpen}
            >
              내 PC에서 첨부
            </CustomizedButton>

            <CustomizedTypography
              fontWeight="500"
              color="textSecondary"
              variant="body1"
            >
              /
            </CustomizedTypography>

            <CustomizedButton
              color="primary"
              variant="text"
              onClick={() => {
                if (isConnectUser) {
                  onOpenDialog('connectDrive');
                } else {
                  onOpenDialog('registerConnect');
                }
              }}
            >
              드라이브에서 첨부
            </CustomizedButton>
          </div>

          <div className="rfq-dropzone-accept-files">
            <CustomizedTypography
              variant="caption"
              color="textPrimary"
              fontWeight="normal"
            >
              {serviceType === CapaQuotationServiceType.Printing
                ? ".STL, .OBJ, .STP, .STEP, .IGS, .IGES 이외의 확장자는 ‘지원하는 파일 확장자'에서 확인해주세요"
                : ServiceTypeInfo[serviceType]?.acceptText}
            </CustomizedTypography>
            <CustomizedTypography
              variant="caption"
              color="textSecondary"
              fontWeight="normal"
            >
              (최대 20개 업로드, 1개당 100MB 이하)
            </CustomizedTypography>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default RequestForQuotationDropzone;
